import { AccountSearch } from "@models/account";
import { AccountCustomDataSearch } from "@models/account-custom-data";

export type AccountInclude = "customData";

export type AccountSortField =
  | "id"
  | "account_name"
  | "account_number"
  | "created_at"
  | "updated_at";

type AccountCustomDataSortField = `custom_data.${number}.field_value`;
type AccountCustomDataSort = AccountCustomDataSortField | `-${AccountCustomDataSortField}`;

export type AccountSort = AccountSortField | `-${AccountSortField}` | AccountCustomDataSort;

export interface SearchParams {
  filter?: Partial<AccountSearch>;
  include?: AccountInclude[];
  sort?: AccountSort[];
  per_page?: number;
  cursor?: string;
  useCache?: boolean;
}

const ACCOUNT_SORT_FIELDS: AccountSortField[] = [
  "id",
  "account_name",
  "account_number",
  "created_at",
  "updated_at",
];

const CUSTOM_DATA_SORT_PATTERN = /^custom_data\.\d+\.field_value$/;

export const ACCOUNT_BROWSE_PER_PAGE = 10;

/**
 * Drops empty custom_data filter rows and assigns stable field_index values.
 * @param customDataArray AccountSearch["custom_data"] | undefined - Raw custom data filters from browse UI.
 * @returns AccountCustomDataSearch[] | undefined - Normalized filters, or undefined when none remain.
 */
function normalizeAccountSearchFilterCustomData(customDataArray: AccountSearch["custom_data"] | undefined): AccountCustomDataSearch[] | undefined {

  if (!customDataArray?.length) {
    return undefined;
  }

  const out: AccountCustomDataSearch[] = [];

  customDataArray.forEach((item, i) => {
    const value = typeof item.field_value === "string" ? item.field_value.trim() : "";

    if (value === "") {
      return;
    }

    const fieldIndex =
      item.field_index !== undefined &&
      item.field_index !== null &&
      Number.isFinite(Number(item.field_index))
        ? Number(item.field_index)
        : i;

    out.push({ field_index: fieldIndex, field_value: value });
  });

  return out.length > 0 ? out : undefined;
}

/**
 * Strips empty custom_data entries and normalizes custom_data filter shape for account search.
 * @param filter AccountSearch - Raw account search filter from browse UI.
 * @returns AccountSearch - Filter safe to send to the accounts search endpoint.
 */
export function normalizeAccountSearchFilter(filter: AccountSearch): AccountSearch {

  // Normalize account custom_data filters (trim, drop blanks, fix indices)
  const custom_data = normalizeAccountSearchFilterCustomData(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;

  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

/**
 * Builds the POST body for the accounts Spatie browse/search endpoint.
 * @param params SearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - Request body fields for accounts/search.
 */
export function getAccountSearchBody(params: SearchParams) {

  const { filter: rawFilter, include, sort, per_page = 20, cursor, useCache } = params;

  // Normalize filter fields (e.g. custom data) for Spatie search API
  const filter = rawFilter ? normalizeAccountSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
    ...(useCache && { use_cache: true }),
  };
}

/**
 * Converts an account sort field and direction into a Spatie sort token.
 * @param field AccountSortField - Account column to sort by.
 * @param order "ASC" | "DESC" - Sort direction from the browse table.
 * @returns AccountSort - Spatie sort token for the given field and order.
 */
export function toAccountSort(field: AccountSortField, order: "ASC" | "DESC"): AccountSort {

  return order === "DESC" ? `-${field}` : field;
}

/**
 * Resolves a browse table sort key into an accounts Spatie sort token.
 * @param sortKey string - Column key from the browse table (may include custom_data paths).
 * @param order "ASC" | "DESC" - Sort direction from the browse table.
 * @returns AccountSort - Spatie sort token accepted by the accounts search API.
 */
export function buildAccountSort(sortKey: string, order: "ASC" | "DESC"): AccountSort {

  const bare = sortKey.replace(/^-/, "");

  if (ACCOUNT_SORT_FIELDS.includes(bare as AccountSortField)) {
    return toAccountSort(bare as AccountSortField, order);
  }

  if (CUSTOM_DATA_SORT_PATTERN.test(bare)) {
    return (order === "DESC" ? `-${bare}` : bare) as AccountSort;
  }

  return toAccountSort("id", order);
}
